import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// Smart backend URL detection
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (!hostname.includes('preview.emergentagent.com') && 
        !hostname.includes('emergent.host') && 
        !hostname.includes('localhost') &&
        !hostname.includes('127.0.0.1')) {
      return window.location.origin;
    }
  }
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  return '';
};

const API = `${getBackendUrl()}/api`;

// Geofencing radius in meters - increased for better reliability
// GPS accuracy can vary from 5m (good) to 100m+ (poor conditions)
const GEOFENCE_RADIUS = 1500; // 1.5km for general validation (increased from 1km)
const PICKUP_HARD_BLOCK_RADIUS = 3000; // 3km - cannot tap "arrived at pickup" if further (increased from 2km)
const DROPOFF_REMINDER_RADIUS = 1500; // 1.5km - trigger reminder if driver moves away from dropoff
const LOCATION_UPDATE_INTERVAL = 15000; // 15 seconds (was 5s - too aggressive, drains battery)
const MIN_ACCURACY_BUFFER = 100; // Minimum accuracy buffer to add (meters)

export function useGPSTracking(token, options = {}) {
  const { enabled = true, rideId = null, updateInterval = LOCATION_UPDATE_INTERVAL } = options;
  
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('checking'); // 'granted', 'denied', 'prompt', 'checking', 'unknown'
  
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);

  // Check permission status - use both permissions API and actual geolocation test
  useEffect(() => {
    const checkPermission = async () => {
      // First, try the permissions API if available
      if ('permissions' in navigator) {
        try {
          const result = await navigator.permissions.query({ name: 'geolocation' });
          setPermissionStatus(result.state);
          result.onchange = () => setPermissionStatus(result.state);
          
          // If permission is granted, we're done
          if (result.state === 'granted') {
            return;
          }
        } catch (error) {
          console.log('Permissions API not available, falling back to geolocation test');
        }
      }
      
      // Fallback: Try to actually get the location to determine permission
      // This works better on iOS/Safari where permissions API may not be available
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            // Successfully got position = permission granted
            setPermissionStatus('granted');
            setCurrentLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              heading: position.coords.heading,
              speed: position.coords.speed,
              timestamp: new Date().toISOString()
            });
          },
          (error) => {
            // Error getting position
            if (error.code === 1) { // PERMISSION_DENIED
              setPermissionStatus('denied');
            } else if (error.code === 2) { // POSITION_UNAVAILABLE
              // Position unavailable but permission might still be granted
              // This can happen if GPS is enabled but device can't get a fix
              setPermissionStatus('granted'); // Treat as granted since user didn't deny
            } else if (error.code === 3) { // TIMEOUT
              // Timeout - GPS might be slow, but permission might be granted
              setPermissionStatus('prompt'); // Let user try again
            }
          },
          {
            enableHighAccuracy: false, // Use low accuracy for quick permission check
            timeout: 5000, // Quick timeout for permission check
            maximumAge: 60000
          }
        );
      } else {
        setPermissionStatus('denied'); // Geolocation not supported
      }
    };
    
    checkPermission();
  }, []);

  // Get current position once
  const getCurrentPosition = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            timestamp: new Date().toISOString()
          };
          setCurrentLocation(loc);
          setLocationError(null);
          resolve(loc);
        },
        (error) => {
          setLocationError(error.message);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 10000
        }
      );
    });
  }, []);

  // Send location to backend
  const sendLocationToBackend = useCallback(async (location) => {
    if (!token) return;
    
    try {
      await axios.post(`${API}/driver/location`, {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        heading: location.heading,
        speed: location.speed,
        ride_id: rideId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to send location:', error);
    }
  }, [token, rideId]);

  // Start continuous tracking
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }

    if (watchIdRef.current !== null) {
      // Already tracking
      return;
    }

    setIsTracking(true);
    setLocationError(null);

    // Watch position changes
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
          timestamp: new Date().toISOString()
        };
        setCurrentLocation(loc);
        setLocationError(null);
      },
      (error) => {
        setLocationError(error.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 5000
      }
    );

    // Also send to backend periodically
    intervalRef.current = setInterval(async () => {
      try {
        const loc = await getCurrentPosition();
        await sendLocationToBackend(loc);
      } catch (e) {
        console.error('Periodic location update failed:', e);
      }
    }, updateInterval);
  }, [getCurrentPosition, sendLocationToBackend, updateInterval]);

  // Stop tracking
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTracking(false);
  }, []);

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = useCallback((lat1, lng1, lat2, lng2) => {
    const R = 6371000; // Earth's radius in meters
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;

    const a = Math.sin(deltaPhi / 2) ** 2 +
              Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  }, []);

  // Check if within geofence - accounts for GPS accuracy
  const isWithinGeofence = useCallback((targetLat, targetLng, radius = GEOFENCE_RADIUS) => {
    if (!currentLocation || !targetLat || !targetLng) {
      return { within: true, distance: 0, message: 'Location unavailable', accuracy: null };
    }

    const distance = calculateDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      targetLat,
      targetLng
    );

    // Account for GPS accuracy - add accuracy as buffer to the allowed radius
    // This means if GPS accuracy is 50m, we add 50m to the allowed radius
    const gpsAccuracy = currentLocation.accuracy || 0;
    const accuracyBuffer = Math.max(gpsAccuracy, MIN_ACCURACY_BUFFER);
    const effectiveRadius = radius + accuracyBuffer;
    
    const isWithin = distance <= effectiveRadius;

    return {
      within: isWithin,
      distance: Math.round(distance),
      accuracy: Math.round(gpsAccuracy),
      effectiveRadius: Math.round(effectiveRadius),
      message: isWithin 
        ? `Within geofence (${Math.round(distance)}m away, GPS accuracy: ±${Math.round(gpsAccuracy)}m)`
        : `Outside geofence (${Math.round(distance)}m away, need to be within ${Math.round(effectiveRadius)}m, GPS accuracy: ±${Math.round(gpsAccuracy)}m)`
    };
  }, [currentLocation, calculateDistance]);

  // Check geofence with backend
  const checkGeofenceWithBackend = useCallback(async (rideIdToCheck, checkType = 'pickup') => {
    if (!token || !currentLocation) {
      return { within_geofence: true, message: 'Location not available' };
    }

    try {
      const response = await axios.post(
        `${API}/rides/${rideIdToCheck}/geofence-check?check_type=${checkType}`,
        {
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Geofence check failed:', error);
      return { within_geofence: true, message: 'Check failed' };
    }
  }, [token, currentLocation]);

  // Request permission
  const requestPermission = useCallback(async () => {
    try {
      await getCurrentPosition();
      return true;
    } catch (error) {
      setLocationError(error.message);
      return false;
    }
  }, [getCurrentPosition]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);

  // Auto-start tracking if enabled
  useEffect(() => {
    if (enabled && permissionStatus === 'granted') {
      startTracking();
    }
    return () => {
      if (!enabled) {
        stopTracking();
      }
    };
  }, [enabled, permissionStatus, startTracking, stopTracking]);

  return {
    currentLocation,
    locationError,
    isTracking,
    permissionStatus,
    startTracking,
    stopTracking,
    getCurrentPosition,
    isWithinGeofence,
    checkGeofenceWithBackend,
    requestPermission,
    calculateDistance,
    GEOFENCE_RADIUS,
    PICKUP_HARD_BLOCK_RADIUS,
    DROPOFF_REMINDER_RADIUS
  };
}

export default useGPSTracking;
